"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getPromiseCached = exports.ApiClient = void 0;
const commons_logs_1 = require("@deliverr/commons-logs");
const ApiErrorSerializers_1 = require("@deliverr/commons-logs/lib/ApiErrorSerializers");
const commons_objects_1 = require("@deliverr/commons-objects");
const axios_1 = require("axios");
const lodash_1 = require("lodash");
const LRU = require("lru-cache");
const Qs = require("qs");
class ApiClient {
    constructor(config) {
        this.dataCache = new LRU({ max: 20000 });
        if (config.authEngine === commons_objects_1.AuthEngine.AUTH0) {
            config.headers = config.headers || {};
            config.headers[commons_objects_1.authEngineHeader] = commons_objects_1.AuthEngine.AUTH0;
        }
        this.axios = axios_1.default.create(config);
        this.axios.interceptors.request.use(async (requestConfig) => {
            if (this.beforeEach) {
                await this.beforeEach();
            }
            requestConfig.headers = Object.assign(Object.assign(Object.assign(Object.assign({}, requestConfig.headers), this.getHeaderFromContext("X-Request-Id")), this.getHeaderFromContext("X-Request-Caller")), this.getHeaderFromContext("X-System-Trace-Ids"));
            if (this.authToken) {
                requestConfig.headers.Authorization = `Bearer ${this.authToken}`;
            }
            return requestConfig;
        });
    }
    withAccessToken(token) {
        this.authToken = token;
        return this;
    }
    addBeforeEach(beforeEach) {
        this.beforeEach = beforeEach;
    }
    getHeaderFromContext(headerId) {
        return commons_logs_1.contextUtils.getContext(headerId) ? { [headerId]: commons_logs_1.contextUtils.getContext(headerId) } : {};
    }
    async getBatch(path, query) {
        return this.axios
            .request({
            url: path,
            params: query,
            paramsSerializer: (params) => Qs.stringify(params, { arrayFormat: "repeat" }),
        })
            .then((res) => res.data)
            .catch((err) => {
            this.handleAxiosError(err, { path, query });
        });
    }
    async get(path, query, headers) {
        return this.axios
            .request({
            url: path,
            params: query,
            headers,
            paramsSerializer: (params) => Qs.stringify(params, { arrayFormat: "repeat" }),
        })
            .then((res) => res.data)
            .catch((err) => {
            this.handleAxiosError(err, { path, query });
        });
    }
    async post(path, body, query) {
        return this.axios
            .request({
            url: path,
            method: "POST",
            data: body,
            params: query,
            paramsSerializer: (params) => Qs.stringify(params, { arrayFormat: "repeat" }),
        })
            .then((res) => res.data)
            .catch((err) => {
            this.handleAxiosError(err, { path, query, body });
        });
    }
    async postWithHeadersInResponse(path, body, query) {
        return this.axios
            .request({
            url: path,
            method: "POST",
            data: body,
            params: query,
            paramsSerializer: (params) => Qs.stringify(params, { arrayFormat: "repeat" }),
        })
            .then((res) => {
            let responseResult = res.data;
            if (!(0, lodash_1.isObject)(responseResult)) {
                responseResult = { originalResult: responseResult };
            }
            responseResult.headers = res.headers;
            return responseResult;
        })
            .catch((err) => {
            this.handleAxiosError(err, { path, query, body });
        });
    }
    async postWithStatusInResponse(path, body, query, headers) {
        return this.axios
            .request({
            url: path,
            method: "POST",
            data: body,
            params: query,
            headers,
            paramsSerializer: (params) => Qs.stringify(params, { arrayFormat: "repeat" }),
        })
            .then((res) => {
            let responseResult = res.data;
            if (!(0, lodash_1.isObject)(responseResult)) {
                responseResult = { originalResult: responseResult };
            }
            responseResult.headers = res.headers;
            responseResult.status = res.status;
            return responseResult;
        })
            .catch((err) => {
            this.handleAxiosError(err, { path, query, body, headers });
        });
    }
    async postWithHeaders(path, body, query, headers) {
        return this.axios
            .request({
            url: path,
            method: "POST",
            data: body,
            params: query,
            headers,
            paramsSerializer: (params) => Qs.stringify(params, { arrayFormat: "repeat" }),
        })
            .then((res) => res.data)
            .catch((err) => {
            this.handleAxiosError(err, { path, query, body, headers });
        });
    }
    async patch(path, body, query) {
        return this.axios
            .request({
            url: path,
            method: "PATCH",
            data: body,
            params: query,
            paramsSerializer: (params) => Qs.stringify(params, { arrayFormat: "repeat" }),
        })
            .then((res) => res.data)
            .catch((err) => {
            this.handleAxiosError(err, { path, query, body });
        });
    }
    async put(path, body, query) {
        return this.axios
            .request({
            url: path,
            method: "PUT",
            data: body,
            params: query,
            paramsSerializer: (params) => Qs.stringify(params, { arrayFormat: "repeat" }),
        })
            .then((res) => res.data)
            .catch((err) => {
            this.handleAxiosError(err, { path, query, body });
        });
    }
    async delete(path, query, body) {
        return this.axios
            .request({
            url: path,
            method: "DELETE",
            data: body,
            params: query,
            paramsSerializer: (params) => Qs.stringify(params, { arrayFormat: "repeat" }),
        })
            .then((res) => res.data)
            .catch((err) => {
            this.handleAxiosError(err, { path, query, body });
        });
    }
    async request(requestConfig) {
        return this.axios.request(requestConfig);
    }
    async getCached(url, params = {}, expiry = 86400000, verbose = false) {
        const serializedParams = Qs.stringify(params, {
            arrayFormat: "repeat",
            sort: (a, b) => a.localeCompare(b),
        });
        const cacheKey = `${url}?${serializedParams}`;
        return getPromiseCached(async () => this.get(url, params), this.dataCache, cacheKey, expiry, verbose);
    }
    maybeGetDeliverrError(err) {
        const deliverrErrorCode = (0, lodash_1.get)(err, "response.data.error.code");
        if (deliverrErrorCode) {
            return new commons_objects_1.DeliverrError({
                code: deliverrErrorCode,
                subcode: (0, lodash_1.get)(err, "response.data.error.subcode"),
                message: (0, lodash_1.get)(err, "response.data.error.message"),
                payload: (0, lodash_1.get)(err, "response.data.error.payload") || (0, ApiErrorSerializers_1.apiErrorSerializer)(err.response),
            });
        }
    }
    maybeGetNetworkError(err) {
        var _a, _b, _c, _d, _e, _f;
        const httpErrorCode = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.status;
        if (httpErrorCode) {
            const baseURL = (_b = err === null || err === void 0 ? void 0 : err.config) === null || _b === void 0 ? void 0 : _b.baseURL;
            const url = (_c = err === null || err === void 0 ? void 0 : err.config) === null || _c === void 0 ? void 0 : _c.url;
            const method = (_d = err === null || err === void 0 ? void 0 : err.config) === null || _d === void 0 ? void 0 : _d.method;
            const message = err === null || err === void 0 ? void 0 : err.message;
            const data = typeof ((_e = err === null || err === void 0 ? void 0 : err.response) === null || _e === void 0 ? void 0 : _e.data) === "string" && ((_f = err === null || err === void 0 ? void 0 : err.response) === null || _f === void 0 ? void 0 : _f.data.slice(0, 50));
            return new commons_objects_1.DeliverrError({
                code: commons_objects_1.HttpErrorCodes[httpErrorCode] || commons_objects_1.ApiError.UNKNOWN_ERROR,
                message: `"${data || message}" on "${method}" to "${baseURL}"`,
                payload: {
                    baseURL,
                    url,
                    method,
                    httpErrorCode,
                    message,
                    data,
                    err,
                },
            });
        }
    }
    getUnknownError(err) {
        return new commons_objects_1.DeliverrError({
            code: commons_objects_1.ApiError.UNKNOWN_ERROR,
            payload: err,
        });
    }
    handleAxiosError(err, context) {
        if (!err.response) {
            commons_logs_1.log.error(Object.assign({ err }, context), "Axios Network Error");
        }
        throw err;
    }
}
exports.ApiClient = ApiClient;
async function getPromiseCached(fetchValue, dataCache, cacheKey, expiry = 86400000, verbose = false) {
    const inCache = dataCache.get(cacheKey);
    if (inCache) {
        if (verbose) {
            commons_logs_1.log.debug(`Returning cached data for ${cacheKey}`);
        }
        return inCache;
    }
    else {
        if (verbose) {
            commons_logs_1.log.info(`Cache for ${cacheKey} expired. Fetching data.`);
        }
        const result = fetchValue();
        dataCache.set(cacheKey, result, expiry);
        result.catch(() => dataCache.del(cacheKey));
        return result;
    }
}
exports.getPromiseCached = getPromiseCached;
//# sourceMappingURL=ApiClient.js.map